/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useEffect, useState, Dispatch} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {RootState} from '../../../../setup'
import * as actions from '../../../../setup/redux/auth/actions'
import './company-switch.scss'
import {SearchComponent} from '../../../../app/services/table/SearchComponent'
import {setActiveCompany} from '../../../../setup/redux/auth/slices'
import {
  isCompanySwitchDisabled,
  isUserRoleDisabled,
  handleNotificationWithToastify,
  redirectToLoginPaymentIfRequired,
  resolveProductionLoginCompanyGate,
  isCompanyGracePeriodExpired,
  sortInspectionCompaniesWithDecentFirst,
} from '../../../../app/services/Functional'
import {endCall} from '../../../../setup/redux/common/slices'
import {resetBillingDetails} from '../../../../setup/redux/common/actions'

const CompanySwitchMenu: FC = () => {
  const navigate = useNavigate()
  const dispatch: Dispatch<any> = useDispatch()
  const {inspectionCompanies, active_inspection_company, user}: any = useSelector<RootState>(
    (state) => ({
      user: state.auth.user,
      inspectionCompanies: state.auth.inspection_companies,
      active_inspection_company: state.auth.active_inspection_company,
    }),
    shallowEqual
  )

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [switching, setSwitching] = useState<boolean>(false)

  useEffect(() => {
    dispatch(actions.getInspectionCompanies())
  }, [])

  const filteredCompanies = (inspectionCompanies ?? []).filter((company: any) => {
    const companyName = company.name ? String(company.name) : ''
    const searchValue = searchTerm ? String(searchTerm.search) : ''

    return (
      isUserRoleDisabled(company) &&
      companyName
        .replace(/_|\s/g, '')
        .toLowerCase()
        .includes(searchValue.replace(/_|\s/g, '').toLowerCase())
    )
  })

  const sortedFilteredCompanies = sortInspectionCompaniesWithDecentFirst(filteredCompanies)

  const reorderedCompanies = (() => {
    const firstCompany = sortedFilteredCompanies[0] || null
    const activeCompany = sortedFilteredCompanies.find(
      (company: any) => company.id === active_inspection_company?.id
    )
    const remainingCompanies = sortedFilteredCompanies.filter(
      (company: any) => company.id !== firstCompany?.id && company.id !== activeCompany?.id
    )

    const result = []
    if (firstCompany) result.push(firstCompany)
    if (activeCompany && activeCompany.id !== firstCompany?.id) result.push(activeCompany)
    return result.concat(remainingCompanies)
  })()

  const applyCompanySwitchGate = async (company: any): Promise<boolean> => {
    const plan = company?.plans?.[0]
    const subscription = plan?.subscription
    const graceExpiredSubscription =
      isCompanyGracePeriodExpired(company) &&
      ((subscription?.type === 'paid' && company?.is_expired) ||
        (subscription?.type === 'free' && company?.is_expired))

    let expiryData = null
    if (graceExpiredSubscription && company?.id) {
      const expiryResp: any = await dispatch(actions.checkSubscriptionExpiry(company.id))
      expiryData = expiryResp?.data
    }

    const gate = resolveProductionLoginCompanyGate(company, {
      requireCompanyAdmin: true,
      graceRequiresExpiredSubscription: true,
      expiryData,
    })

    if (gate.action === 'not_company_admin') {
      navigate('/error/auth')
      return true
    }
    if (gate.action === 'redirect') {
      if (gate.resetBilling) {
        dispatch(resetBillingDetails())
      }
      dispatch(endCall({}))
      window.location.href = gate.url
      return true
    }
    if (gate.action === 'access_denied') {
      dispatch(endCall({}))
      dispatch(actions.handleIsExpiredCompanyAPIcalling(true))
      dispatch(actions.handleExpiredCompany(gate.company))
      navigate('/error/access-denied')
      return true
    }
    return false
  }

  const handleSwitchCompany = async (selectedCompany: any) => {
    if (!user?.is_root && isCompanySwitchDisabled(selectedCompany)) {
      return
    }

    if (user?.is_root) {
      dispatch(setActiveCompany(selectedCompany))
      return setTimeout(() => {
        window.location.href = '/'
      }, 100)
    }

    const membershipId = selectedCompany.membership_id
    if (!membershipId) {
      if (await applyCompanySwitchGate(selectedCompany)) {
        return
      }
      dispatch(setActiveCompany(selectedCompany))
      return setTimeout(() => {
        window.location.href = '/'
      }, 100)
    }

    setSwitching(true)
    const response: any = await dispatch(actions.switchWorkspace(membershipId))

    if (redirectToLoginPaymentIfRequired(response?.data)) {
      setSwitching(false)
      return
    }

    if (!response?.data?.success || !response?.data?.data) {
      setSwitching(false)
      handleNotificationWithToastify({
        success: false,
        message: response?.data?.message || 'Failed to switch workspace',
      })
      return
    }

    const hydrated = actions.hydrateWorkspaceAuth(
      response.data.data,
      user,
      inspectionCompanies,
    )
    dispatch(actions.handleSetWorkspaceAuth(hydrated))

    const companiesResp: any = await dispatch(actions.getInspectionCompanies(selectedCompany.id))
    const allCompanies = companiesResp?.data?.data?.inspection_companies
    const activeCompany =
      allCompanies?.find((c: any) => c.id === selectedCompany.id) || selectedCompany

    if (redirectToLoginPaymentIfRequired(null, activeCompany)) {
      setSwitching(false)
      return
    }

    if (await applyCompanySwitchGate(activeCompany)) {
      setSwitching(false)
      return
    }

    dispatch(setActiveCompany(activeCompany))
    setSwitching(false)
    setTimeout(() => {
      window.location.href = '/'
    }, 100)
  }

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg menu-state-primary fw-bold cs-menu-container fs-6 w-275px'
      data-kt-menu='true'
    >
      <div className='d-flex flex-column flex-gap-16'>
        <div className='d-flex flex-column flex-gap-8'>
          <div className='font-size-12 text-uppercase font-weight-bold line-height-20 text-soft-steel-gray'>
            Companies
          </div>
          <div className='cs-search'>
            <SearchComponent updateState={setSearchTerm} />
          </div>
        </div>
        <div className='com-list-container'>
          {switching ? (
            <div className='d-flex justify-content-center align-items-center py-4 gap-2'>
              <span className='spinner-border spinner-border-sm text-primary' />
              <span className='text-soft-steel-gray'>Switching...</span>
            </div>
          ) : (
            <>
              {reorderedCompanies &&
                reorderedCompanies.map((inspectionCompany: any, index: number) => {
                  const disabled = user?.is_root
                    ? false
                    : isCompanySwitchDisabled(inspectionCompany)
                  return (
                  <React.Fragment key={index}>
                    <li className='cs-form-check'>
                      <input
                        className={`cs-form-check-input cs-form-disabled-check-input`}
                        type='radio'
                        name='company'
                        id={`company-${index}`}
                        disabled={disabled}
                        value={inspectionCompany?.id}
                        checked={active_inspection_company?.id === inspectionCompany?.id}
                        onChange={() => {
                          if (!disabled) {
                            handleSwitchCompany(inspectionCompany)
                          }
                        }}
                      />
                      <label
                        className={`cs-form-check-label ${disabled ? 'disable-company-name-container' : ''}`}
                        htmlFor={disabled ? undefined : `company-${index}`}
                      >
                        <span className='sw-com-name'>{inspectionCompany.name}</span>
                      </label>
                    </li>
                  </React.Fragment>
                  )
                })}
              {reorderedCompanies?.length === 0 && (
                <div className='text-soft-steel-gray'>No companies found</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export {CompanySwitchMenu}
